import axios from 'axios';
import store from '../store/';

const config = require('../config/environment.json');

const useLocalBackend = String(process.env.VUE_APP_USE_LOCAL_BACKEND || '').toLowerCase() === 'true'
  || process.env.VUE_APP_USE_LOCAL_BACKEND === '1';
const apiBaseURL = useLocalBackend
  ? (process.env.VUE_APP_LOCAL_API_URL || 'http://localhost:8081')
  : config.api.baseURL;

/** ========= Axios instances ========= **/
const api = axios.create({
  baseURL: apiBaseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

const authApi = axios.create({
  baseURL: apiBaseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

/** ========= State for refresh flow ========= **/
let isRefreshing = false;
let pendingQueue = [];
let sessionExpired = false;
let authFailureCount = 0;
const AUTH_FAILURE_LIMIT = 3;

/** ========= Helpers ========= **/
const norm = (s = '') => String(s || '').toLowerCase();

const getAccessToken = () =>
  store.state.userPortalWeflix?.acessToken || store.state.token;

const getRefreshToken = () =>
  store.state.userPortalWeflix?.refreshToken || store.state.refreshToken;

const commitAccessToken = (newAccessToken) => {
  if (store.state.token || !store.state.userPortalWeflix) {
    store.commit('SET_TOKEN', newAccessToken);
  }
  if (store.state.userPortalWeflix) {
    store.commit('SET_USER_PORTAL_WEFLIX', {
      ...store.state.userPortalWeflix,
      acessToken: newAccessToken,
    });
  }
};

const enqueue = (cb) => pendingQueue.push(cb);
const flushQueue = (error, token) => {
  pendingQueue.forEach((cb) => cb(error, token));
  pendingQueue = [];
};

function noteAuthFailure() {
  authFailureCount += 1;
  return authFailureCount >= AUTH_FAILURE_LIMIT;
}

function resetAuthFailures() {
  authFailureCount = 0;
}

function isRefreshAuthFailure(status) {
  return status === 401 || status === 403;
}

/** Garante Authorization em TODAS as variações de headers do Axios v1 */
function setAuthHeader(config, token) {
  config.headers = config.headers || {};

  delete config.headers.Authorization;
  delete config.headers.authorization;

  const method = (config.method || 'get').toLowerCase();
  if (config.headers.common) {
    delete config.headers.common.Authorization;
    delete config.headers.common.authorization;
  }
  if (config.headers[method]) {
    delete config.headers[method].Authorization;
    delete config.headers[method].authorization;
  }

  config.headers.Authorization = `Bearer ${token}`;

  return config;
}

function logoutUser() {
  if (sessionExpired) return;
  sessionExpired = true;
  isRefreshing = false;
  pendingQueue = [];
  console.log('[AUTH] Logout → sessão encerrada');
  try {
    store.dispatch('logout');
  } catch (e) {
    store.commit('SET_TOKEN', null);
    store.commit('SET_REFRESH_TOKEN', null);
  }
  localStorage.removeItem('vuex');
  localStorage.removeItem('activeIndex');
  localStorage.removeItem('logado');
  const path = window.location.pathname || '';
  if (path === '/login' || path === '/portal' || path.startsWith('/esqueci-senha')) {
    return;
  }
  window.location.href = path.startsWith('/aluno') ? '/portal' : '/login';
}

/** ========= Core: refresh + retry ========= **/
async function refreshTokenAndRetry(originalRequest) {
  console.log('[AUTH] refreshTokenAndRetry → start', {
    url: originalRequest?.url,
    _retry: originalRequest?._retry,
  });

  if (sessionExpired) {
    return Promise.reject(new Error('Sessão encerrada'));
  }

  if (isRefreshing) {
    console.log('[AUTH] Já em refresh: enfileirando retry');
    return new Promise((resolve, reject) => {
      enqueue((err, newToken) => {
        if (err) return reject(err);
        const cfg = setAuthHeader({ ...originalRequest }, newToken);
        console.log('[AUTH] Retentando após refresh pendente:', cfg.url);
        resolve(api.request(cfg));
      });
    });
  }

  const rToken = getRefreshToken();
  if (!rToken) {
    console.log('[AUTH] Sem refresh token.');
    logoutUser();
    return Promise.reject(new Error('Sem refresh token'));
  }

  isRefreshing = true;
  try {
    console.log('[AUTH] Solicitando refresh…');
    const { data: newAccessToken } = await authApi.post('/auth/refresh', null, {
      headers: { Authorization: `Bearer ${rToken}` },
    });

    console.log('[AUTH] Refresh OK.');
    resetAuthFailures();
    commitAccessToken(newAccessToken);

    isRefreshing = false;
    flushQueue(null, newAccessToken);

    const cfg = setAuthHeader({ ...originalRequest }, newAccessToken);
    console.log('[AUTH] Retentando requisição original:', cfg.url);
    return api.request(cfg);
  } catch (err) {
    console.log('[AUTH] Refresh falhou:', err);
    isRefreshing = false;
    flushQueue(err, null);
    const refreshStatus = Number(err?.response?.status) || null;
    if (isRefreshAuthFailure(refreshStatus)) {
      logoutUser();
    } else {
      console.log('[AUTH] Refresh falhou sem logout (servidor/rede):', refreshStatus);
    }
    throw err;
  }
}

/** ========= REQUEST interceptor ========= **/
api.interceptors.request.use(
  (cfg) => {
    if (sessionExpired) {
      return Promise.reject(new Error('Sessão encerrada'));
    }
    console.log('[HTTP][REQ]', {
      method: (cfg.method || 'GET').toUpperCase(),
      url: cfg.url,
    });
    return cfg;
  },
  (error) => {
    console.log('[HTTP][REQ][ERRO]', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (res) => {
    resetAuthFailures();
    console.log('[HTTP][RES]', { url: res.config?.url, status: res.status });
    return res;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const status = Number(error.response?.status) || null;
    const reqUrl = norm(originalRequest.url);

    console.log('[HTTP][RES][ERRO]', {
      status,
      url: reqUrl,
      _retry: originalRequest._retry,
    });

    if (sessionExpired) {
      return Promise.reject(error);
    }

    if (reqUrl.includes('/auth/refresh') && isRefreshAuthFailure(status)) {
      logoutUser();
      return Promise.reject(error);
    }

    const ignoredApiEndpoints = [
      '/auth/validate',
      '/auth/login',
      '/public/',
      '/ecommerce/insert',
    ];
    const shouldIgnoreEndpoint = ignoredApiEndpoints.some((s) =>
      reqUrl.includes(norm(s))
    );

    if (status === 401) {
      noteAuthFailure();
    }

    if (originalRequest.skipAuthRecovery || shouldIgnoreEndpoint) {
      if (status === 401 && authFailureCount >= AUTH_FAILURE_LIMIT) {
        logoutUser();
      }
      return Promise.reject(error);
    }

    if (status >= 500 && status < 600) {
      console.log('[AUTH] 5xx (sem logout):', status, reqUrl);
      return Promise.reject(error);
    }

    if (status === 400) {
      return Promise.reject(error);
    }

    if (status === 403) {
      console.log('[AUTH] 403 forbidden (sem logout):', reqUrl);
      return Promise.reject(error);
    }

    if (status === 401) {
      if (originalRequest._retry) {
        console.log('[AUTH] 401 já retentado.');
        logoutUser();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        return await refreshTokenAndRetry(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
